// composables/useFixedSummaryBar.js
// Reserve a FIXED (non-scrolling) summary bar's height as page-content top padding on
// a Framework7 `:page-content="false"` page — the shared mechanism behind BOTH box-first
// supplier order pages' summary strips (SupplierDailyOrdersPage's `.daily-orders-overview`
// and SupplierBoxDetailPage's `.sbd-summary`). Consuming it from one place keeps the two
// pages' summary behaviour byte-identical.
//
// WHY (P5 live/e2e verification, 2026-07-07 — see the supplier-daily-orders memory):
// a summary `<div>` placed before a `height:100%` `<f7-page-content>` with only
// `flex-shrink: 0` is SILENTLY BROKEN. Framework7's `.page` is `position:absolute;
// display:block` — NOT a flex container (verified in framework7-bundle.css + the F7-Vue
// Page render for page-content=false, which emits a plain `<div class="page">` with no
// flex) — so `flex-shrink` is inert, and the `height:100%` page-content sibling sits
// AFTER the bar in normal flow and overflows the page bottom by the bar's height. The
// last scroll item is then clipped and unreachable (measured live: a 60px bar hid the
// last card by exactly 60px). `.page` not being flex also means a normal-flow bar starts
// at the page top, occluded by the (absolutely-positioned) navbar.
//
// THE FIX this composable enables (all three verified live against the real F7 CSS):
//   1. The bar is `position:absolute; top: var(--f7-page-navbar-offset)` so it pins just
//      below the navbar and never scrolls (it lives outside the page-content scroller).
//   2. Its measured height is reserved as `--f7-page-content-extra-padding-top` (summed
//      into `.page-content`'s padding-top by F7 core CSS), so nothing is clipped and the
//      last item is fully reachable.
//   3. Because the bar is out of flow, `.page-content` spans the FULL page height behind
//      it, so hide-navbar-on-scroll reveals scrolled content in the band the retracting
//      navbar vacates — NO blank strip (unlike a `position:sticky; top:0` first child).
//
// A ResizeObserver keeps the reservation in sync as the bar reflows (locale/RTL, a nudge
// chip appearing, font metrics). `--f7-page-content-extra-padding-top` is written via a
// `:style` binding on `<f7-page>` (F7-Vue forwards it to the `.page` root; the custom
// property inherits down to `.page-content`).
import { computed, onScopeDispose, ref } from 'vue';

export function useFixedSummaryBar() {
  // Measured border-box height of the summary bar (0 while it is not rendered).
  const summaryHeight = ref(0);
  let ro = null;
  let el = null;

  function measure() {
    summaryHeight.value = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
  }

  // Function ref — bind on the summary element via `:ref`. Vue calls it with the element
  // on mount and `null` on unmount, so it transparently handles the bar's `v-if` toggle:
  // (re)observe on appear, disconnect + zero the reservation on disappear.
  function setSummaryEl(next) {
    const nextEl = next || null;
    if (nextEl === el) return;
    if (ro) {
      ro.disconnect();
      ro = null;
    }
    el = nextEl;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    measure();
  }

  // Bind on `<f7-page>` as `:style`. Emits the reservation var only when the bar is
  // present, so a page with no summary keeps page-content's default padding.
  const pageStyleVars = computed(() =>
    summaryHeight.value > 0
      ? { '--f7-page-content-extra-padding-top': `${summaryHeight.value}px` }
      : {},
  );

  onScopeDispose(() => {
    if (ro) ro.disconnect();
    ro = null;
    el = null;
  });

  return { setSummaryEl, summaryHeight, pageStyleVars };
}
